import { useEffect, useState } from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useTypedParams } from 'react-router-typesafe-routes/dom';

import { useResource } from 'app/common/api';
import { usePageNotify } from 'app/common/notifications/features/page-notifications';
import { TTerminalProfile } from 'app/terminals/domains/profiles/core/profiles';
import { appRoute } from 'app/terminals/router/routes';
import { Loading } from 'app/ui';

import { TTerminal, TTerminalType } from '../../core/terminal';
import { getTerminalRequest } from '../../requests/get-terminal.request';
import { updateTerminalRequest } from '../../requests/update-terminal.request';
import { TerminalForm } from '../terminal-form/terminal-form';

type TerminalType = Pick<TTerminalType, 'id'>;
type TerminalProfile = Pick<TTerminalProfile, 'id'>;

type UpdatedTerminal = Omit<TTerminal, 'type' | 'profile'> & {
  type: TerminalType;
  profile?: TerminalProfile;
};

type FormFields = {
  type: TerminalType;
  profile?: TerminalProfile;
  blocked: boolean;
  dsn: string;
  token: string;
};

type TerminalId = Pick<TTerminal, 'id'>;

export const DetailsTerminal = observer(() => {
  const { id } = useTypedParams(appRoute.TERMINAL);
  const notify = usePageNotify();
  const [fields] = useState(() =>
    observable.box<FormFields>({
      blocked: false,
      dsn: '',
      profile: { id: 0 },
      token: '',
      type: { id: 0 },
    })
  );

  const getProfileResource = useResource<TerminalId, TTerminal>({
    request: getTerminalRequest,
    errorNotify: notify,
    onDone: (result) => {
      fields.set(result);
    },
  });

  const updateProfileResource = useResource<UpdatedTerminal, TTerminal>({
    request: updateTerminalRequest,
    errorNotify: notify,
    onDone: (result) => {
      fields.set(result);

      notify.addMessage({
        type: 'success',
        message: 'Данные терминала успешно обновлен',
      });
    },
  });

  const handleSubmit = (fields: Omit<UpdatedTerminal, 'id'>) => {
    const updatedProfile: UpdatedTerminal = {
      ...fields,
      id: Number(id),
    };

    updateProfileResource.send(updatedProfile);
  };

  const isLoading =
    getProfileResource.isLoading || updateProfileResource.isLoading;

  useEffect(() => {
    getProfileResource.send({ id: id || 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Loading active={isLoading}>
      <TerminalForm
        onSubmit={handleSubmit}
        values={fields.get()}
        dsnReadOnluly
      />
    </Loading>
  );
});
